import React, { useMemo } from "react";
import { FaMagic, FaPlus, FaTrash, FaSave } from "react-icons/fa";
import AdminModal from "./AdminModal";
import DataTable from "./DataTable";
import StatusBadge from "./StatusBadge";
import { colors, spacing, typography, inputBase, btnPrimary, btnSecondary, btnDanger, btnGhost } from "../utils/designTokens";
import {
  TIPOS_DOCUMENTO,
  ORIENTACIONES,
  CAMPOS_OCUPANTES,
  estadosDeFilas,
  camposInvalidos,
  cambiosFisicosParaExistente,
  filaVacia,
} from "../utils/borradorUnidades";

/**
 * Revisión de la carga de unidades con IA: tabla editable a pantalla casi
 * completa. Cada cambio sube por onChange (EditarEdificio lo guarda como
 * borrador), así que cerrar no pierde nada: "Seguir después" solo cierra.
 *
 * Unidades que ya existen (mismo número y piso): se actualizan sus datos
 * físicos; los ocupantes se asignan desde la ficha de la unidad.
 */

const BADGES = {
  nueva: { status: "completo", label: "Nueva" },
  existe: { status: "cerrada", label: "Ya existe" },
  repetida: { status: "pendiente", label: "Repetida" },
  incompleta: { status: "error", label: "Falta número o piso" },
};

// [campo, encabezado, tipo, ancho en px]
const COLUMNAS = [
  ["number", "Número", "texto", 76],
  ["floor", "Piso", "texto", 56],
  ["m2", "m²", "decimal", 76],
  ["valorCatastral", "V. catastral", "decimal", 96],
  ["bedroom", "Dorm.", "entero", 56],
  ["bathroom", "Baños", "entero", 56],
  ["balcony", "Balcón", "sino", 72],
  ["garage", "Garage", "sino", 72],
  ["infront", "Orientación", "orientacion", 128],
  ["propNombre", "Prop. nombre", "texto", 150],
  ["propApellido", "Prop. apellido", "texto", 150],
  ["propTipoDoc", "Prop. tipo doc.", "doc", 104],
  ["propCedula", "Prop. documento", "texto", 120],
  ["propCelular", "Prop. celular", "tel", 124],
  ["propEmail", "Prop. email", "email", 200],
  ["inqNombre", "Inq. nombre", "texto", 150],
  ["inqApellido", "Inq. apellido", "texto", 150],
  ["inqTipoDoc", "Inq. tipo doc.", "doc", 104],
  ["inqCedula", "Inq. documento", "texto", 120],
  ["inqCelular", "Inq. celular", "tel", 124],
  ["inqEmail", "Inq. email", "email", 200],
];
const OCUPANTES = new Set(CAMPOS_OCUPANTES);

const horaLegible = (ms) =>
  ms ? new Date(ms).toLocaleString("es-UY", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : null;

function Celda({ fila, campo, etiqueta, tipo, ancho, invalido, deshabilitado, onCambio }) {
  const props = {
    value: fila[campo] ?? "",
    onChange: (e) => onCambio(fila._id, campo, e.target.value),
    "aria-label": `${etiqueta}, unidad ${fila.number || "sin número"}`,
    "aria-invalid": invalido || undefined,
    disabled: deshabilitado,
    title: deshabilitado ? "La unidad ya existe: asigná los ocupantes desde su ficha." : undefined,
    className: "habitia-input",
    style: {
      ...S.input,
      width: ancho,
      ...(invalido ? S.inputInvalido : {}),
      ...(deshabilitado ? S.inputDeshabilitado : {}),
    },
  };
  if (tipo === "sino") {
    return (
      <select {...props}>
        <option value="">—</option>
        <option value="si">Sí</option>
        <option value="no">No</option>
      </select>
    );
  }
  if (tipo === "orientacion" || tipo === "doc") {
    const opciones = tipo === "doc" ? TIPOS_DOCUMENTO : ORIENTACIONES;
    return (
      <select {...props}>
        <option value="">{tipo === "doc" ? "CI" : "—"}</option>
        {opciones.filter((o) => !(tipo === "doc" && o === "CI")).map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    );
  }
  const tipoInput = tipo === "email" ? "email" : tipo === "tel" ? "tel" : "text";
  const inputMode = tipo === "decimal" ? "decimal" : tipo === "entero" ? "numeric" : undefined;
  return <input {...props} type={tipoInput} inputMode={inputMode} />;
}

export default function RevisionUnidadesModal({
  open,
  filas,
  onChange,
  unidadesExistentes,
  actualizadoEn,
  guardando,
  progreso,
  onGuardar,
  onCerrar,
  onDescartar,
}) {
  const estados = useMemo(() => estadosDeFilas(filas, unidadesExistentes), [filas, unidadesExistentes]);
  const invalidos = useMemo(() => new Map(filas.map((f) => [f._id, camposInvalidos(f)])), [filas]);

  const resumen = useMemo(() => {
    const r = { nueva: 0, existe: 0, repetida: 0, incompleta: 0, aActualizar: 0, invalidas: 0 };
    filas.forEach((f) => {
      const e = estados.get(f._id) || {};
      r[e.estado] = (r[e.estado] || 0) + 1;
      if (e.estado === "existe" && Object.keys(cambiosFisicosParaExistente(f, e.unidad)).length) r.aActualizar += 1;
      if ((invalidos.get(f._id) || new Set()).size) r.invalidas += 1;
    });
    return r;
  }, [filas, estados, invalidos]);

  const cambiar = (id, campo, valor) => onChange(filas.map((f) => (f._id === id ? { ...f, [campo]: valor } : f)));
  const eliminar = (id) => onChange(filas.filter((f) => f._id !== id));
  const agregar = () => onChange([...filas, filaVacia()]);
  const quitarExistentes = () => onChange(filas.filter((f) => (estados.get(f._id) || {}).estado !== "existe"));

  const bloqueos = [];
  if (resumen.incompleta) bloqueos.push(`${resumen.incompleta} sin número o piso`);
  if (resumen.repetida) bloqueos.push(`${resumen.repetida} repetidas`);
  if (resumen.invalidas) bloqueos.push(`${resumen.invalidas} con datos para corregir (en rojo)`);
  const aGuardar = resumen.nueva + resumen.aActualizar;
  const puedeGuardar = !guardando && bloqueos.length === 0 && aGuardar > 0;

  const columnas = [
    ...COLUMNAS.slice(0, 2),
    {
      key: "_estado",
      label: "Estado",
      render: (_, fila) => {
        const b = BADGES[(estados.get(fila._id) || {}).estado] || BADGES.nueva;
        return (
          <span style={S.sinCorte}>
            <StatusBadge status={b.status} label={b.label} size="sm" />
          </span>
        );
      },
    },
    ...COLUMNAS.slice(2),
  ].map((c) => {
    if (!Array.isArray(c)) return c;
    const [campo, etiqueta, tipo, ancho] = c;
    return {
      key: campo,
      label: etiqueta,
      render: (_, fila) => (
        <Celda
          fila={fila}
          campo={campo}
          etiqueta={etiqueta}
          tipo={tipo}
          ancho={ancho}
          invalido={(invalidos.get(fila._id) || new Set()).has(campo)}
          deshabilitado={guardando || (OCUPANTES.has(campo) && (estados.get(fila._id) || {}).estado === "existe")}
          onCambio={cambiar}
        />
      ),
    };
  });
  columnas.push({
    key: "_acciones",
    label: "",
    render: (_, fila) => (
      <button
        type="button"
        style={S.btnEliminar}
        className="habitia-btn-ghost"
        onClick={() => eliminar(fila._id)}
        disabled={guardando}
        aria-label={`Eliminar unidad ${fila.number || "sin número"}`}
      >
        <FaTrash size={12} />
      </button>
    ),
  });

  const guardadoEn = horaLegible(actualizadoEn);

  return (
    <AdminModal
      open={open}
      onClose={guardando ? () => {} : onCerrar}
      hideClose={guardando}
      size="full"
      icon={<FaMagic />}
      title="Revisar y completar unidades"
      subtitle={`${filas.length} ${filas.length === 1 ? "unidad" : "unidades"}${guardadoEn ? ` · borrador guardado ${guardadoEn}` : ""}`}
      footer={
        <>
          <span style={S.footerNota}>
            {bloqueos.length ? `Antes de guardar: ${bloqueos.join(", ")}.` : "Los cambios se guardan solos en este navegador."}
          </span>
          <button type="button" style={btnDanger} className="habitia-btn-danger" onClick={onDescartar} disabled={guardando}>
            Descartar borrador
          </button>
          <button type="button" style={btnSecondary} className="habitia-btn-secondary" onClick={onCerrar} disabled={guardando}>
            Seguir después
          </button>
          <button
            type="button"
            style={{ ...btnPrimary, ...(puedeGuardar ? {} : S.btnDeshabilitado) }}
            className="habitia-btn-primary"
            onClick={onGuardar}
            disabled={!puedeGuardar}
          >
            <FaSave size={14} />
            {guardando
              ? `Guardando${progreso ? ` ${progreso.hechas}/${progreso.total}` : ""}...`
              : `Guardar ${resumen.nueva} nuevas${resumen.aActualizar ? ` y actualizar ${resumen.aActualizar}` : ""}`}
          </button>
        </>
      }
    >
      <div style={S.barra}>
        <div style={S.chips}>
          <StatusBadge status="completo" label={`${resumen.nueva} nuevas`} size="sm" />
          {resumen.existe > 0 && <StatusBadge status="cerrada" label={`${resumen.existe} ya existen`} size="sm" />}
          {resumen.repetida > 0 && <StatusBadge status="pendiente" label={`${resumen.repetida} repetidas`} size="sm" />}
          {resumen.incompleta > 0 && <StatusBadge status="error" label={`${resumen.incompleta} sin número o piso`} size="sm" />}
        </div>
        <div style={S.chips}>
          {resumen.existe > 0 && (
            <button type="button" style={btnSecondary} className="habitia-btn-secondary" onClick={quitarExistentes} disabled={guardando}>
              Quitar las que ya existen
            </button>
          )}
          <button type="button" style={btnSecondary} className="habitia-btn-secondary" onClick={agregar} disabled={guardando}>
            <FaPlus size={12} /> Agregar unidad
          </button>
        </div>
      </div>
      {resumen.existe > 0 && (
        <p style={S.nota}>
          Las que ya existen no se crean de nuevo: se actualizan los datos de la unidad que completes (una celda vacía no borra nada).
          Sus ocupantes se asignan desde la ficha de cada unidad.
        </p>
      )}
      <DataTable
        columns={columnas}
        data={filas.map((f) => ({ ...f, id: f._id }))}
        dense
        stickyFirstColumn
        maxHeight="calc(94vh - 260px)"
        emptyTitle="No quedan unidades en el borrador"
        emptyDescription="Agregá una unidad o procesá otro documento con IA."
      />
    </AdminModal>
  );
}

const S = {
  input: {
    ...inputBase,
    padding: "6px 8px",
    fontSize: typography.sizes.base,
  },
  inputInvalido: {
    borderColor: colors.error,
    background: colors.errorLight,
  },
  inputDeshabilitado: {
    background: colors.gray100,
    color: colors.gray400,
    cursor: "not-allowed",
  },
  barra: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing[3],
    flexWrap: "wrap",
    marginBottom: spacing[3],
  },
  chips: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    flexWrap: "wrap",
  },
  nota: {
    fontSize: typography.sizes.base,
    color: colors.gray600,
    margin: `0 0 ${spacing[3]}px`,
  },
  footerNota: {
    marginRight: "auto",
    alignSelf: "center",
    fontSize: typography.sizes.base,
    color: colors.gray600,
  },
  btnEliminar: {
    ...btnGhost,
    color: colors.errorDark,
  },
  btnDeshabilitado: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
  sinCorte: {
    display: "inline-block",
    whiteSpace: "nowrap",
  },
};
